import { UploadMediaFile } from '@anm/api/modules/uploads';
import createUpload from '@anm/helpers/createUpload';
import noop from '@anm/helpers/noop';
import useSwitchState from '@anm/hooks/useSwitchState';
import useUploadChecker from '@anm/hooks/useUploadChecker';
import { useEffect, useState } from 'react';

import { Preview, PreviewImageProps, PreviewSelectProps, UploadPreviewProps } from '../../../types';

const MAX_ALLOWED_IMAGE_SIZE = 2 * 1024 * 1024;
const IDEAL_IMAGE_SIZE = 500 * 1024;

const getUploadPreviewSrc = (upload: UploadMediaFile) => {
  const biggestFirst = upload.files.sort((a, b) => b.file.info.size - a.file.info.size);

  let file = biggestFirst.find(
    s => s.file.info.size <= IDEAL_IMAGE_SIZE && Math.min(s.file.info.height, s.file.info.width) >= 1024
  );
  if (!file) {
    file = biggestFirst.find(s => s.file.info.size <= MAX_ALLOWED_IMAGE_SIZE);
  }
  return file?.request.url;
};

const useUploadPreview = ({ onUpload, onUploadStart = noop, onUploadUpdate = noop, onError }: UploadPreviewProps) => {
  const [uploadId, setUploadId] = useState('');

  useEffect(
    () => () => {
      setUploadId('');
      onUploadUpdate({ isPending: false, error: undefined, isUploaded: undefined });
    },
    []
  );

  const createNewUpload = async (previewFile: File) => {
    try {
      onUploadStart();
      const uploadId = await createUpload({ file: previewFile, nameSpace: 'service', simpleUpload: false });

      uploadId && setUploadId(uploadId);
    } catch (e) {
      onError();
    }
  };

  const onUploadStable = (upload: UploadMediaFile) => {
    const preview = getUploadPreviewSrc(upload);

    if (upload.files.length > 0 && preview) {
      onUpload(preview);
      onUploadUpdate({ isPending: false, isUploaded: true });
    } else {
      onUploadUpdate({
        isPending: false,
        error: { code: 440, message: 'Failed to upload image. Please try again later' }
      });
    }
  };

  const checkSmallImages = (uploadEntity: UploadMediaFile) => {
    const hasImagesUnderLimit = uploadEntity.files.some(
      f => f.file.fileType === 'image' && f.file.info.size <= MAX_ALLOWED_IMAGE_SIZE
    );

    return hasImagesUnderLimit;
  };

  useUploadChecker({
    uploadId,
    onUploadStart,
    onUploadError: onError,
    onReady: onUploadStable,
    checkReady: checkSmallImages
  });

  return createNewUpload;
};

const usePreviewImage = ({
  defaultPreview,
  isEditDestination,
  defaultDestinationPreview = '',
  onUploadUpdate
}: PreviewImageProps) => {
  const [isCropModalOpen, openCropModal, closeCropModal] = useSwitchState();
  const [preview, setPreview] = useState<Preview>({ src: defaultPreview, file: null });
  const [destinationPreview, setDestinationPreview] = useState<Preview>({ src: defaultDestinationPreview, file: null });

  const previewSetter = isEditDestination ? setDestinationPreview : setPreview;

  useEffect(() => {
    setDestinationPreview({ src: isEditDestination ? defaultDestinationPreview : '', file: null });
  }, [isEditDestination]);

  const onUpload = (previewSrc: string) => {
    previewSetter({ file: null, src: previewSrc });
  };

  const onUploadStart = () => {
    onUploadUpdate({ isPending: true, isUploaded: false });
    closeCropModal();
  };

  const createUpload = useUploadPreview({
    onUpload,
    onUploadStart,
    onUploadUpdate,
    onError: () => {
      onUploadUpdate({ isPending: false, error: { code: 441, message: 'Something went wrong while uploading!' } });
      previewSetter({ src: isEditDestination ? defaultDestinationPreview : defaultPreview, file: null });
    }
  });

  const handlePreviewSelect = ({ file, objectUrl, isEditDestination }: PreviewSelectProps) => {
    const image = new Image();
    image.crossOrigin = '*';
    image.src = objectUrl;

    const previewSetter = isEditDestination ? setDestinationPreview : setPreview;

    image.onload = function() {
      const img = this as HTMLImageElement;
      const height = img.height;
      const width = img.width;

      const cropRatio = Math.abs(+(width / height).toFixed(2) - +(16 / 9).toFixed(2));
      const canCrop = cropRatio >= 0.03;

      if (canCrop) {
        const stateCb = (prev: Preview) => ({ file, src: prev.src });
        previewSetter(stateCb);
        !destinationPreview.src && setDestinationPreview(stateCb);
        openCropModal();
      } else {
        const newPreview = { file: null, src: objectUrl };

        !destinationPreview.src && setDestinationPreview(newPreview);
        previewSetter(newPreview);

        createUpload(file);
      }
    };
  };

  return [preview, destinationPreview, isCropModalOpen, handlePreviewSelect, closeCropModal] as const;
};

export default usePreviewImage;
